import { Fragment, useState } from 'react';

import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { type PhoebeActivityHighlight } from '@libraries/typescript/api_client';

const MAX_ROWS = 8;
const INTERNAL_NOTE_PREFIX = /^\[(?:unrouted agent note|attribute)\]\s*/i;
const GENERIC_CAREGIVER_PREFIX = /^Caregiver\s+(?=[a-z])/;
const GENERIC_CAREGIVER_POSSESSIVE_PREFIX = /^Caregiver's\s+/i;

type FeedRow = {
    readonly caregiverId: string;
    readonly caregiverName: string;
    readonly note: string;
    readonly at: number;
};

function timeAgo(epochMs: number): string {
    const days = Math.floor((Date.now() - epochMs) / 86_400_000);
    if (days <= 0) {
        return 'today';
    }
    if (days === 1) {
        return 'yesterday';
    }
    if (days < 7) {
        return `${days} days ago`;
    }
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? 'a week ago' : `${weeks} weeks ago`;
}

function formatNoteContent(content: string): string {
    return content
        .trim()
        .replace(INTERNAL_NOTE_PREFIX, '')
        .replace(GENERIC_CAREGIVER_POSSESSIVE_PREFIX, '’s ')
        .replace(GENERIC_CAREGIVER_PREFIX, '');
}

// The name leads the row, so a note that already starts with the
// caregiver's name ("Eric prefers mornings") would read "Eric Eric prefers
// mornings" — drop the leading name and keep the rest of the sentence.
function noteAfterName(note: string, caregiverName: string): string {
    const candidates = [
        caregiverName.trim(),
        caregiverName.trim().split(/\s+/)[0],
    ];
    for (const name of candidates) {
        if (
            name &&
            note.toLowerCase().startsWith(name.toLowerCase()) &&
            note.length > name.length &&
            // Only strip at a word boundary: "Sam" must not eat into
            // "Samantha covered the shift."
            /^[\s,.'’:;-]/.test(note.charAt(name.length))
        ) {
            return note.slice(name.length).trimStart();
        }
    }
    return note;
}

function buildRows(
    highlights: ReadonlyArray<PhoebeActivityHighlight>,
): ReadonlyArray<FeedRow> {
    return highlights
        .map((highlight) => ({
            caregiverId: highlight.caregiver_id,
            caregiverName: highlight.caregiver_name,
            note: noteAfterName(
                formatNoteContent(highlight.content),
                highlight.caregiver_name,
            ),
            at: Date.parse(highlight.learned_at),
        }))
        .sort((a, b) => b.at - a.at)
        .slice(0, MAX_ROWS);
}

/**
 * Recent caregiver notes Phoebe saved while filling shifts. The section
 * around it owns the heading and any banner.
 */
export function LearnedNotesList({
    highlights,
    organizationId,
}: {
    readonly highlights: ReadonlyArray<PhoebeActivityHighlight>;
    readonly organizationId: string | null;
}) {
    const { formatName } = useFormatPersonName();
    const rows = buildRows(highlights);
    const [selectedCaregiverId, setSelectedCaregiverId] = useState<
        string | null
    >(null);
    if (rows.length === 0) {
        return (
            <p className="text-muted-foreground py-2 text-sm">
                Phoebe’s recent caregiver notes will appear here.
            </p>
        );
    }
    return (
        <Fragment>
            <ul className="divide-y">
                {rows.map((row) => {
                    return (
                        <li
                            key={`${formatName(row.caregiverName)}-${row.note}-${row.at}`}
                            className="flex items-baseline gap-4 py-3"
                        >
                            <p className="min-w-0 flex-1 text-sm break-words">
                                <button
                                    type="button"
                                    className="text-foreground cursor-pointer font-medium underline-offset-4 hover:underline"
                                    onClick={() =>
                                        setSelectedCaregiverId(row.caregiverId)
                                    }
                                >
                                    {formatName(row.caregiverName)}
                                </button>
                                {row.note.startsWith('’s ') ? '' : ' '}
                                {row.note}
                            </p>
                            <span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap">
                                {timeAgo(row.at)}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {organizationId ? (
                <CaregiverDetailModal
                    isOpen={selectedCaregiverId !== null}
                    onClose={() => setSelectedCaregiverId(null)}
                    organizationId={organizationId}
                    caregiverId={selectedCaregiverId ?? undefined}
                />
            ) : null}
        </Fragment>
    );
}
